import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Download, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Check if file type can be previewed
const isPreviewable = (filename) => {
  const ext = filename.toLowerCase().split('.').pop();
  return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
};

const isImage = (filename) => {
  const ext = filename.toLowerCase().split('.').pop();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
};

const isPDF = (filename) => {
  const ext = filename.toLowerCase().split('.').pop();
  return ext === 'pdf';
};

export const DocumentPreview = ({ 
  document, 
  loadId, 
  isOpen, 
  onClose,
  documents = [],
  onNavigate
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  // Find current document index for navigation
  const currentIndex = documents.findIndex(d => d.id === document?.id);
  const hasNext = currentIndex < documents.length - 1;
  const hasPrev = currentIndex > 0;

  useEffect(() => {
    if (isOpen && document) {
      loadDocument();
    }
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [isOpen, document?.id]);

  const loadDocument = async () => {
    setLoading(true);
    setError(null);
    setZoom(1);
    setRotation(0);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/loads/${loadId}/documents/${document.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = URL.createObjectURL(response.data);
      setBlobUrl(url);
    } catch (err) {
      setError('Failed to load document');
      console.error('Preview error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (blobUrl) {
      const link = window.document.createElement('a');
      link.href = blobUrl;
      link.download = document.original_filename;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  const handleZoomIn = () => setZoom(z => Math.min(z + 0.25, 3));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.25, 0.5));
  const handleRotate = () => setRotation(r => (r + 90) % 360);

  const navigatePrev = () => {
    if (hasPrev && onNavigate) {
      onNavigate(documents[currentIndex - 1]);
    }
  };

  const navigateNext = () => {
    if (hasNext && onNavigate) {
      onNavigate(documents[currentIndex + 1]);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowLeft') navigatePrev();
      if (e.key === 'ArrowRight') navigateNext();
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-') handleZoomOut();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex]);

  if (!document) return null;

  const canPreview = isPreviewable(document.original_filename);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-[95vw] h-[90vh] bg-[#0A0E1A] border-white/10 p-0 flex flex-col">
        {/* Header */}
        <DialogHeader className="px-4 py-3 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-white truncate pr-4">
              {document.original_filename}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {/* Navigation */}
              {documents.length > 1 && (
                <div className="flex items-center gap-1 mr-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={navigatePrev}
                    disabled={!hasPrev}
                    className="text-slate-400 hover:text-white h-8 w-8 p-0"
                  >
                    <ChevronLeft size={18} />
                  </Button>
                  <span className="text-slate-400 text-sm">
                    {currentIndex + 1} / {documents.length}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={navigateNext}
                    disabled={!hasNext}
                    className="text-slate-400 hover:text-white h-8 w-8 p-0"
                  >
                    <ChevronRight size={18} />
                  </Button>
                </div>
              )}
              
              {/* Image controls */}
              {isImage(document.original_filename) && blobUrl && (
                <div className="flex items-center gap-1 border-r border-white/10 pr-3 mr-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleZoomOut}
                    className="text-slate-400 hover:text-white h-8 w-8 p-0"
                    title="Zoom out"
                  >
                    <ZoomOut size={16} />
                  </Button>
                  <span className="text-slate-400 text-xs w-12 text-center">
                    {Math.round(zoom * 100)}%
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleZoomIn}
                    className="text-slate-400 hover:text-white h-8 w-8 p-0"
                    title="Zoom in"
                  >
                    <ZoomIn size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRotate}
                    className="text-slate-400 hover:text-white h-8 w-8 p-0"
                    title="Rotate"
                  >
                    <RotateCw size={16} />
                  </Button>
                </div>
              )}
              
              {/* Download */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownload}
                className="text-[#00A6E8] hover:text-[#00A6E8]/80 h-8"
              >
                <Download size={16} className="mr-1" /> Download
              </Button>
              
              {/* Close */}
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-slate-400 hover:text-white h-8 w-8 p-0"
              >
                <X size={18} />
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        {/* Content */}
        <div className="flex-1 overflow-auto bg-[#1E293B]/50 flex items-center justify-center p-4">
          {loading && (
            <div className="text-slate-400">
              <div className="animate-spin h-8 w-8 border-2 border-[#00A6E8] border-t-transparent rounded-full mx-auto mb-2"></div>
              Loading preview...
            </div>
          )}
          
          {error && (
            <div className="text-center">
              <p className="text-red-400 mb-4">{error}</p>
              <Button onClick={loadDocument} variant="outline" className="border-white/20 text-slate-300">
                Try Again
              </Button>
            </div>
          )}
          
          {!loading && !error && !canPreview && (
            <div className="text-center">
              <div className="text-4xl mb-4">📄</div>
              <p className="text-slate-400 mb-4">
                Preview not available for this file type.
              </p>
              <Button onClick={handleDownload} className="bg-[#00A6E8] text-[#0A0E1A]">
                <Download size={16} className="mr-2" /> Download to View
              </Button>
            </div>
          )}
          
          {!loading && !error && blobUrl && isImage(document.original_filename) && (
            <div 
              className="flex items-center justify-center w-full h-full overflow-auto"
              style={{ 
                cursor: zoom > 1 ? 'grab' : 'default'
              }}
            >
              <img
                src={blobUrl}
                alt={document.original_filename}
                className="max-w-full max-h-full object-contain transition-transform duration-200"
                style={{
                  transform: `scale(${zoom}) rotate(${rotation}deg)`,
                  transformOrigin: 'center center'
                }}
                draggable={false}
              />
            </div>
          )}
          
          {!loading && !error && blobUrl && isPDF(document.original_filename) && (
            <iframe
              src={blobUrl}
              className="w-full h-full border-0 rounded-lg"
              title={document.original_filename}
            />
          )}
        </div>
        
        {/* Footer with file info */}
        <div className="px-4 py-2 border-t border-white/10 bg-[#0F172A] flex-shrink-0">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>
              {document.file_type} • {formatFileSize(document.file_size)}
            </span>
            <span>
              Uploaded: {new Date(document.uploaded_at).toLocaleString()}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Helper function
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Export helper for checking if preview is available
export { isPreviewable };
